import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ScrollToTopButton from '../components/ScrollToTopButton';
import BrandLogo from '../components/BrandLogo';
import {
    Sparkles, ArrowRight, ArrowLeft, ArrowDown,
    Brain, Target, Briefcase, Shuffle,
    Search, Layers,
    FileText, Upload, Cpu, Award, ListChecks,
    Building2, Users, UserCheck, Rocket,
    Eye, Zap, Shield, Heart,
} from 'lucide-react';

const BRAND = 'GUIRETAL';
const PARENT = 'NIMVEXA';

// ----------------------------------------------------------------------------
// Small local presentational primitives (kept in-file — one-off shapes only
// used on this page, no reuse elsewhere).
// ----------------------------------------------------------------------------

const SectionTitle = ({ eyebrow, title, description, center = true, testid }) => (
    <div className={`${center ? 'text-center' : ''} max-w-3xl ${center ? 'mx-auto' : ''} mb-12 sm:mb-16`} data-testid={testid}>
        {eyebrow && (
            <p className="text-sm font-semibold uppercase tracking-wider text-[#002FA7] mb-3">{eyebrow}</p>
        )}
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] font-['Outfit'] tracking-tight leading-tight">
            {title}
        </h2>
        {description && (
            <p className="mt-4 text-base sm:text-lg text-[#4B5563] leading-relaxed">{description}</p>
        )}
    </div>
);

const IconCard = ({ icon: Icon, title, description, testid }) => (
    <div
        className="bg-white border border-[#E5E7EB] rounded-xl p-6 sm:p-7 hover:border-[#002FA7]/30 hover:shadow-md transition-all"
        data-testid={testid}
    >
        <div className="w-11 h-11 rounded-lg bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center mb-4">
            <Icon className="w-5 h-5" />
        </div>
        <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-2">{title}</h3>
        <p className="text-sm text-[#4B5563] leading-relaxed">{description}</p>
    </div>
);

// ----------------------------------------------------------------------------

export default function AboutPage() {
    const { t, i18n } = useTranslation();

    // SEO: title + meta description swap when the page mounts and revert on unmount.
    useEffect(() => {
        document.title = t('about.seo_title');
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute('content', t('about.seo_description'));
        }
        return () => {
            // Restore the app-wide values in the CURRENT language (not a stale copy).
            document.title = i18n.t('app.document_title');
            if (meta) meta.setAttribute('content', i18n.t('app.document_description'));
        };
    }, [t, i18n.language]);

    const analysisCards = [
        { icon: Brain,      k: 'about.analysis_1' },
        { icon: Target,     k: 'about.analysis_2' },
        { icon: Briefcase,  k: 'about.analysis_3' },
        { icon: Shuffle,    k: 'about.analysis_4' },
    ];

    const audienceCards = [
        { icon: Building2, k: 'about.for_1' },
        { icon: Users,     k: 'about.for_2' },
        { icon: UserCheck, k: 'about.for_3' },
        { icon: Rocket,    k: 'about.for_4' },
    ];

    const engagementCards = [
        { icon: Target, k: 'about.eng_1' },
        { icon: Eye,    k: 'about.eng_2' },
        { icon: Zap,    k: 'about.eng_3' },
        { icon: Shield, k: 'about.eng_4' },
    ];

    const flowSteps = [
        { icon: FileText, k: 'about.flow_1' },
        { icon: Upload,   k: 'about.flow_2' },
        { icon: Cpu,      k: 'about.flow_3' },
        { icon: Award,    k: 'about.flow_4' },
    ];

    const sectors = t('about.sectors_list', { returnObjects: true }) || [];
    const scoreMetrics = t('about.score_metrics', { returnObjects: true }) || [];
    const techList = t('about.tech_list', { returnObjects: true }) || [];

    return (
        <div className="min-h-screen bg-white" data-testid="about-page">
            {/* ------------------------------------------------------------ */}
            {/* Nav — GUIRETAL branded (this page only, per rebrand request). */}
            {/* ------------------------------------------------------------ */}
            <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[#E5E7EB]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
                    <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="about-nav-home">
                        <BrandLogo size={36} />
                        <span className="text-xl font-bold text-[#0A0A0A] font-['Outfit']">{BRAND}</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <LanguageSwitcher />
                        <Link to="/">
                            <Button variant="ghost" size="sm" data-testid="about-back-btn">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                {t('about.back_home')}
                            </Button>
                        </Link>
                        <Link to="/register" className="hidden sm:block">
                            <Button size="sm" className="bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="about-nav-cta">
                                {t('about.cta_primary')}
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* ------------------------------------------------------------ */}
            {/* 1. Hero                                                       */}
            {/* ------------------------------------------------------------ */}
            <section className="pt-16 pb-20 sm:pt-24 sm:pb-28 px-4 sm:px-6 bg-gradient-to-b from-[#F5F7FF] via-white to-white">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="space-y-7">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold tracking-wide uppercase">
                            <Sparkles className="w-3.5 h-3.5" />
                            {t('about.hero_eyebrow')}
                        </span>
                        <h1
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A0A0A] font-['Outfit'] tracking-tight leading-tight"
                            data-testid="about-hero-title"
                        >
                            {t('about.hero_title_1')}<br />
                            <span className="text-[#002FA7]">{t('about.hero_title_2')}</span>
                        </h1>
                        <p className="text-base sm:text-lg text-[#4B5563] max-w-xl leading-relaxed">
                            {t('about.hero_desc')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <Link to="/register">
                                <Button size="lg" className="bg-[#002FA7] hover:bg-[#002480] text-white h-12 px-7" data-testid="about-hero-cta-primary">
                                    {t('about.cta_primary')}
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                            <a href="#flow">
                                <Button size="lg" variant="outline" className="h-12 px-7 border-[#E5E7EB]" data-testid="about-hero-cta-secondary">
                                    {t('about.cta_secondary')}
                                </Button>
                            </a>
                        </div>
                    </div>

                    {/* Illustration: layered abstract "candidate matching" cards */}
                    <div className="relative hidden lg:block" aria-hidden="true">
                        <div className="absolute -inset-4 bg-gradient-to-br from-[#002FA7]/10 via-[#3B82F6]/10 to-transparent rounded-3xl blur-3xl" />
                        <div className="relative bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xl">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-10 h-10 rounded-lg bg-[#002FA7] text-white flex items-center justify-center">
                                    <Brain className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-xs text-[#6B7280] uppercase tracking-wider">{t('about.hero_widget_label')}</p>
                                    <p className="text-sm font-semibold text-[#0A0A0A]">{t('about.hero_widget_title')}</p>
                                </div>
                            </div>
                            <div className="space-y-3">
                                {[92, 87, 78].map((score, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
                                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002FA7] to-[#3B82F6]" />
                                        <div className="flex-1">
                                            <div className="h-2 rounded bg-[#E5E7EB] w-3/4 mb-1.5" />
                                            <div className="h-2 rounded bg-[#E5E7EB] w-1/2" />
                                        </div>
                                        <div className="text-right">
                                            <p className="text-lg font-bold text-[#002FA7] font-['Outfit'] leading-none">{score}</p>
                                            <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">score</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 2. Le recrutement évolue                                       */}
            {/* ------------------------------------------------------------ */}
            <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle
                        eyebrow={t('about.evolves_eyebrow')}
                        title={t('about.evolves_title')}
                        testid="about-evolves-header"
                    />
                    <div className="max-w-3xl mx-auto space-y-5 text-[#4B5563] text-base sm:text-lg leading-relaxed mb-12">
                        <p>{t('about.evolves_p1')}</p>
                        <p>{t('about.evolves_p2')}</p>
                        <p className="font-medium text-[#0A0A0A]">{t('about.evolves_p3')}</p>
                        <p>{t('about.evolves_p4')}</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
                        <div className="border border-[#E5E7EB] rounded-xl p-6 bg-[#F9FAFB]" data-testid="about-compare-ats">
                            <div className="w-11 h-11 rounded-lg bg-[#E5E7EB] text-[#6B7280] flex items-center justify-center mb-4">
                                <Search className="w-5 h-5" />
                            </div>
                            <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-2">{t('about.compare_ats_title')}</h3>
                            <p className="text-sm text-[#4B5563] leading-relaxed">{t('about.compare_ats_desc')}</p>
                        </div>
                        <div className="border-2 border-[#002FA7] rounded-xl p-6 bg-white shadow-md relative" data-testid="about-compare-guiretal">
                            <div className="w-11 h-11 rounded-lg bg-[#002FA7] text-white flex items-center justify-center mb-4">
                                <Layers className="w-5 h-5" />
                            </div>
                            <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-2">{t('about.compare_guiretal_title')}</h3>
                            <p className="text-sm text-[#4B5563] leading-relaxed">{t('about.compare_guiretal_desc')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 3. Une analyse intelligente des candidats                     */}
            {/* ------------------------------------------------------------ */}
            <section className="py-16 sm:py-20 px-4 sm:px-6 bg-[#F9FAFB]">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle
                        eyebrow={t('about.analysis_eyebrow')}
                        title={t('about.analysis_title')}
                        testid="about-analysis-header"
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {analysisCards.map((c, i) => (
                            <IconCard
                                key={c.k}
                                icon={c.icon}
                                title={t(`${c.k}_title`)}
                                description={t(`${c.k}_desc`)}
                                testid={`about-analysis-card-${i + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 4. Une plateforme pensée pour tous les métiers                */}
            {/* ------------------------------------------------------------ */}
            <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle
                        eyebrow={t('about.sectors_eyebrow')}
                        title={t('about.sectors_title')}
                        description={t('about.sectors_desc')}
                        testid="about-sectors-header"
                    />
                    <div className="flex flex-wrap justify-center gap-2.5 max-w-4xl mx-auto mb-10" data-testid="about-sectors-list">
                        {sectors.map((sector, i) => (
                            <span
                                key={i}
                                className="inline-flex items-center px-4 py-2 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] text-sm text-[#0A0A0A] font-medium hover:border-[#002FA7]/40 hover:bg-[#002FA7]/5 transition-colors"
                                data-testid={`about-sector-badge-${i}`}
                            >
                                {sector}
                            </span>
                        ))}
                    </div>
                    <p className="text-center text-[#4B5563] max-w-3xl mx-auto italic">
                        {t('about.sectors_note')}
                    </p>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 5. Du CV au talent (flow)                                     */}
            {/* ------------------------------------------------------------ */}
            <section id="flow" className="py-16 sm:py-20 px-4 sm:px-6 bg-[#F9FAFB]">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle
                        eyebrow={t('about.flow_eyebrow')}
                        title={t('about.flow_title')}
                        testid="about-flow-header"
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="about-flow-steps">
                        {flowSteps.map(({ icon: Icon, k }, i) => (
                            <div key={k} className="relative bg-white border border-[#E5E7EB] rounded-xl p-6" data-testid={`about-flow-step-${i + 1}`}>
                                <div className="absolute -top-3 left-6 px-2.5 py-0.5 rounded-full bg-[#002FA7] text-white text-xs font-bold font-['Outfit'] tracking-wider">
                                    {String(i + 1).padStart(2, '0')}
                                </div>
                                <div className="w-11 h-11 rounded-lg bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center mb-4 mt-2">
                                    <Icon className="w-5 h-5" />
                                </div>
                                <h3 className="text-base font-semibold text-[#0A0A0A] font-['Outfit'] mb-2">{t(`${k}_title`)}</h3>
                                <p className="text-sm text-[#4B5563] leading-relaxed">{t(`${k}_desc`)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 6. Plus qu'un score                                           */}
            {/* ------------------------------------------------------------ */}
            <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle
                        eyebrow={t('about.score_eyebrow')}
                        title={t('about.score_title')}
                        description={t('about.score_desc')}
                        testid="about-score-header"
                    />
                    <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="about-score-metrics">
                        {scoreMetrics.map((metric, i) => (
                            <div key={i} className="flex items-start gap-2.5 p-4 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]" data-testid={`about-score-metric-${i}`}>
                                <ListChecks className="w-4 h-4 text-[#002FA7] flex-shrink-0 mt-0.5" />
                                <span className="text-sm text-[#0A0A0A] font-medium leading-tight">{metric}</span>
                            </div>
                        ))}
                    </div>
                    <div className="max-w-3xl mx-auto text-center px-6 py-8 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#3B82F6] text-white">
                        <p className="text-xl sm:text-2xl font-bold font-['Outfit'] leading-snug">
                            {t('about.score_highlight')}
                        </p>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 7. Une technologie au service des recruteurs                  */}
            {/* ------------------------------------------------------------ */}
            <section className="py-16 sm:py-20 px-4 sm:px-6 bg-[#F9FAFB]">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle
                        eyebrow={t('about.tech_eyebrow')}
                        title={t('about.tech_title')}
                        description={t('about.tech_desc')}
                        testid="about-tech-header"
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="about-tech-list">
                        {techList.map((item, i) => (
                            <div key={i} className="flex items-start gap-3 bg-white border border-[#E5E7EB] rounded-xl p-5" data-testid={`about-tech-${i}`}>
                                <div className="w-9 h-9 rounded-lg bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center flex-shrink-0">
                                    <Zap className="w-4 h-4" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-[#0A0A0A] font-['Outfit']">{item.title}</h3>
                                    <p className="text-xs text-[#4B5563] mt-0.5 leading-relaxed">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 8. Conçu pour les entreprises                                  */}
            {/* ------------------------------------------------------------ */}
            <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle
                        eyebrow={t('about.for_eyebrow')}
                        title={t('about.for_title')}
                        testid="about-for-header"
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {audienceCards.map((c, i) => (
                            <IconCard
                                key={c.k}
                                icon={c.icon}
                                title={t(`${c.k}_title`)}
                                description={t(`${c.k}_desc`)}
                                testid={`about-for-card-${i + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 9. Notre vision                                                */}
            {/* ------------------------------------------------------------ */}
            <section className="py-20 sm:py-24 px-4 sm:px-6 bg-[#0A0A0A] text-white" data-testid="about-vision">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/10 text-white mb-6">
                        <Heart className="w-6 h-6" />
                    </div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-[#3B82F6] mb-4">{t('about.vision_eyebrow')}</p>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] tracking-tight leading-tight mb-8">
                        {t('about.vision_title')}
                    </h2>
                    <div className="space-y-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-3xl mx-auto">
                        <p>{t('about.vision_p1')}</p>
                        <p className="text-white font-medium">{t('about.vision_p2')}</p>
                        <p className="italic">{t('about.vision_p3')}</p>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 10. GUIRETAL & NIMVEXA                                        */}
            {/* ------------------------------------------------------------ */}
            <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle
                        eyebrow={t('about.brand_eyebrow')}
                        title={t('about.brand_title')}
                        testid="about-brand-header"
                    />
                    <div className="max-w-3xl mx-auto space-y-5 text-base sm:text-lg text-[#4B5563] leading-relaxed mb-12">
                        <p>{t('about.brand_p1')}</p>
                        <p>{t('about.brand_p2')}</p>
                        <p>{t('about.brand_p3')}</p>
                    </div>
                    <div className="flex flex-col items-center gap-4 max-w-md mx-auto" data-testid="about-brand-tree">
                        <div className="w-full text-center p-6 rounded-2xl bg-gradient-to-br from-[#0A0A0A] to-[#1F2937] text-white shadow-lg">
                            <p className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight">{PARENT}</p>
                            <p className="text-xs uppercase tracking-wider text-white/60 mt-1">{t('about.brand_parent_role')}</p>
                        </div>
                        <ArrowDown className="w-6 h-6 text-[#002FA7]" />
                        <div className="w-full text-center p-6 rounded-2xl bg-[#002FA7] text-white shadow-lg">
                            <p className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight">{BRAND}</p>
                            <p className="text-xs uppercase tracking-wider text-white/70 mt-1">{t('about.brand_product_role')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 11. Notre engagement                                           */}
            {/* ------------------------------------------------------------ */}
            <section className="py-16 sm:py-20 px-4 sm:px-6 bg-[#F9FAFB]">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle
                        eyebrow={t('about.engagement_eyebrow')}
                        title={t('about.engagement_title')}
                        testid="about-engagement-header"
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {engagementCards.map((c, i) => (
                            <IconCard
                                key={c.k}
                                icon={c.icon}
                                title={t(`${c.k}_title`)}
                                description={t(`${c.k}_desc`)}
                                testid={`about-engagement-card-${i + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* 12. CTA final                                                  */}
            {/* ------------------------------------------------------------ */}
            <section className="py-20 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#002FA7] via-[#002480] to-[#001B5C] text-white">
                <div className="max-w-4xl mx-auto text-center space-y-6">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] tracking-tight leading-tight" data-testid="about-cta-title">
                        {t('about.cta_title')}
                    </h2>
                    <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
                        {t('about.cta_desc')}
                    </p>
                    <div className="pt-4">
                        <Link to="/register">
                            <Button size="lg" className="bg-white text-[#002FA7] hover:bg-white/90 h-12 px-8 font-semibold" data-testid="about-cta-button">
                                {t('about.cta_primary')}
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </Link>
                    </div>
                    <p className="text-sm text-white/60 pt-4">
                        {t('about.cta_footer', { parent: PARENT })}
                    </p>
                </div>
            </section>

            {/* Minimal footer */}
            <footer className="py-8 px-4 sm:px-6 border-t border-[#E5E7EB] bg-white">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#6B7280]">
                    <div className="flex items-center gap-2">
                        <BrandLogo size={24} rounded="rounded" />
                        <span>{t('landing.footer_copyright')}</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/" className="hover:text-[#0A0A0A]">{t('about.back_home')}</Link>
                        <Link to="/terms" className="hover:text-[#0A0A0A]">{t('nav.terms', 'Terms')}</Link>
                    </div>
                </div>
            </footer>

            <ScrollToTopButton />
        </div>
    );
}
